import { useState, useEffect } from 'react';
import type { Item, AuctionState, Listing, SaleMode, SaleType, BinSale } from '../types';
import { createAuction, resetUnsoldItem, createBinSale, startBinSale } from '../api/client';

interface AuctionParams {
  startingPrice: number;
  auctionDuration: number;
  counterbidTime: number;
  suddenDeathEnabled: boolean;
}

interface AuctionControlsProps {
  showId: string | null;
  selectedItem: Item | null;
  selectedListing: Listing | null;
  /** Server-side on-deck item — populated from WS SHOW_STATE on page reload
   *  even when the client hasn't picked anything locally. We use this to
   *  recover the Start button after a reload mid-show. */
  onDeckItem: Item | null;
  activeAuction: AuctionState | null;
  activeBinSale: BinSale | null;
  auctionEnded: { winnerId: string | null; winnerUsername: string | null; winningBid: number | null } | null;
  onRunAnotherAuction?: (listing: Listing, params: AuctionParams) => Promise<void>;
}

type ConfigureStatus = 'on_deck' | 'sold' | 'no_winner';

export function AuctionControls({
  showId,
  selectedItem,
  selectedListing,
  onDeckItem,
  activeAuction,
  activeBinSale,
  auctionEnded,
  onRunAnotherAuction,
}: AuctionControlsProps) {
  // The "active item" — the thing we'd act on when Start Auction is clicked.
  // Prefer the client-side selection; fall back to the server's on-deck item
  // (which is what survives a page reload).
  const activeItem = selectedItem ?? onDeckItem;
  // Sale mode
  const [saleMode, setSaleMode] = useState<SaleMode>('AUCTION');

  // Auction params
  const [startingPrice, setStartingPrice] = useState(10);
  const [auctionDuration, setAuctionDuration] = useState(15);
  const [counterbidTime, setCounterbidTime] = useState(10);
  const [suddenDeathEnabled, setSuddenDeathEnabled] = useState(false);

  // BIN params
  const [binPrice, setBinPrice] = useState(10);
  const [binQuantity, setBinQuantity] = useState(1);
  const [binSaleEnabled, setBinSaleEnabled] = useState(false);
  const [binSaleType, setBinSaleType] = useState<SaleType>('PERCENT');
  const [binSaleAmount, setBinSaleAmount] = useState(10);

  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // For sold / no_winner states, params are collapsed by default; the user
  // expands them via the "Edit params" button. on_deck always shows params.
  const [userExpanded, setUserExpanded] = useState(false);

  // Sync starting price with the active item / listing
  useEffect(() => {
    if (activeItem) {
      setStartingPrice(activeItem.startingPrice || 10);
      setBinPrice(activeItem.startingPrice || 10);
    } else if (selectedListing) {
      setStartingPrice(selectedListing.startingPrice || 10);
      setBinPrice(selectedListing.startingPrice || 10);
    }
  }, [activeItem, selectedListing]);

  // Reset the user-expanded flag whenever the underlying item changes —
  // each ended auction starts collapsed again.
  useEffect(() => {
    setUserExpanded(false);
  }, [activeItem?.id, auctionEnded?.winnerId]);

  // Clear error on state transitions (successful action)
  useEffect(() => {
    setError(null);
  }, [activeAuction, activeBinSale, auctionEnded]);

  // The configure panel is hidden while an auction or BIN sale is live —
  // AuctionOverlay owns the live UI. We only show controls when the seller
  // is choosing a price/duration before starting (or restarting) something.
  const status: ConfigureStatus | null = (() => {
    if (activeAuction || (activeBinSale && activeBinSale.status === 'LIVE')) return null;
    if (auctionEnded) return auctionEnded.winnerId ? 'sold' : 'no_winner';
    if (activeItem) return 'on_deck';
    return null;
  })();

  if (status === null) return null;

  const canStartAuction = status === 'on_deck' && saleMode === 'AUCTION';
  const canStartBinSale = status === 'on_deck' && saleMode === 'BUY_IT_NOW';
  const canRunAnother = status === 'sold' && selectedListing && selectedListing.stock > 0;
  const canRunAgain = status === 'no_winner' && activeItem;
  const showAuctionPanel = canStartAuction || canRunAnother || canRunAgain;
  const showBinPanel = canStartBinSale;
  const showModeSelector = status === 'on_deck';
  // Params are always expanded for on_deck; collapsed by default after a
  // sold / no_winner auction unless the user hits "Edit params".
  const paramsExpanded = status === 'on_deck' || userExpanded;
  // Show the next show-wide item number on the "Start item #N" button.
  const nextItemNumber = (activeItem?.itemNumber ?? 0) + 1;
  const runAnotherLabel = nextItemNumber > 1
    ? `Start item #${nextItemNumber}`
    : 'Run Another Auction';

  const handleStartAuction = async () => {
    if (!showId || !activeItem) return;
    setLoading(true);
    setError(null);
    try {
      await createAuction(
        showId,
        activeItem.id,
        startingPrice,
        auctionDuration,
        counterbidTime,
        suddenDeathEnabled,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to start auction');
      console.error('Failed to start auction:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleRunAnotherAuction = async () => {
    if (!selectedListing || !onRunAnotherAuction) return;
    if (selectedListing.stock <= 0) {
      setError('This listing is out of stock');
      return;
    }
    setActionLoading('runAnother');
    setError(null);
    try {
      await onRunAnotherAuction(selectedListing, {
        startingPrice,
        auctionDuration,
        counterbidTime,
        suddenDeathEnabled,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to run another auction');
      console.error('Failed to run another auction:', e);
    } finally {
      setActionLoading(null);
    }
  };

  const handleRunAuctionAgain = async () => {
    if (!showId || !activeItem) return;
    setActionLoading('runAgain');
    setError(null);
    try {
      await resetUnsoldItem(showId, activeItem.id);
      await createAuction(
        showId,
        activeItem.id,
        startingPrice,
        auctionDuration,
        counterbidTime,
        suddenDeathEnabled,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to run auction again');
      console.error('Failed to run auction again:', e);
    } finally {
      setActionLoading(null);
    }
  };

  const handleStartBinSale = async () => {
    if (!showId || !activeItem) return;
    setLoading(true);
    setError(null);
    try {
      const sale = await createBinSale(showId, {
        itemId: activeItem.id,
        basePrice: binPrice,
        quantity: binQuantity,
        saleEnabled: binSaleEnabled,
        saleType: binSaleEnabled ? binSaleType : undefined,
        saleAmount: binSaleEnabled ? binSaleAmount : undefined,
      });
      await startBinSale(showId, sale.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to start sale');
      console.error('Failed to start BIN sale:', e);
    } finally {
      setLoading(false);
    }
  };

  const calculateFinalPrice = (): number => {
    if (!binSaleEnabled) return binPrice;
    if (binSaleType === 'PERCENT') return binPrice * (1 - binSaleAmount / 100);
    return Math.max(0, binPrice - binSaleAmount);
  };

  return (
    <div style={styles.container}>
      {error && <div style={styles.errorMessage}>{error}</div>}

      {showModeSelector && (
        <div style={styles.modeSelector}>
          <button
            onClick={() => setSaleMode('AUCTION')}
            style={{ ...styles.modeButton, ...(saleMode === 'AUCTION' ? styles.modeButtonActive : {}) }}
          >
            Auction
          </button>
          <button
            onClick={() => setSaleMode('BUY_IT_NOW')}
            style={{ ...styles.modeButton, ...(saleMode === 'BUY_IT_NOW' ? styles.modeButtonActive : {}) }}
          >
            Buy It Now
          </button>
        </div>
      )}

      {showAuctionPanel && (
        <div style={styles.panel}>
          {paramsExpanded && (
            <>
              <div style={styles.paramRow}>
                <div style={styles.paramField}>
                  <label style={styles.paramLabel}>Starting Price</label>
                  <div style={styles.priceInputWrapper}>
                    <span style={styles.pricePrefix}>$</span>
                    <input
                      type="number"
                      value={startingPrice}
                      onChange={(e) => setStartingPrice(Number(e.target.value))}
                      min={1}
                      style={styles.input}
                    />
                  </div>
                </div>
                <div style={styles.paramField}>
                  <label style={styles.paramLabel}>Duration</label>
                  <div style={styles.priceInputWrapper}>
                    <input
                      type="number"
                      value={auctionDuration}
                      onChange={(e) => setAuctionDuration(Number(e.target.value))}
                      min={15}
                      max={300}
                      style={styles.input}
                    />
                    <span style={styles.suffix}>sec</span>
                  </div>
                </div>
              </div>

              <div style={styles.counterbidSection}>
                <div style={styles.counterbidLabelRow}>
                  <label style={styles.paramLabel}>Counterbid Time</label>
                  {suddenDeathEnabled && (
                    <span style={styles.suddenDeathPill}>Sudden Death enabled</span>
                  )}
                </div>
                <div style={styles.counterbidButtons}>
                  <button
                    onClick={() => setSuddenDeathEnabled(true)}
                    style={{
                      ...styles.counterbidButton,
                      ...(suddenDeathEnabled ? styles.counterbidButtonActive : {}),
                    }}
                    title="Sudden death — no time extensions on bids"
                  >
                    Off
                  </button>
                  {[3, 5, 10].map((seconds) => (
                    <button
                      key={seconds}
                      onClick={() => {
                        setSuddenDeathEnabled(false);
                        setCounterbidTime(seconds);
                      }}
                      style={{
                        ...styles.counterbidButton,
                        ...(!suddenDeathEnabled && counterbidTime === seconds ? styles.counterbidButtonActive : {}),
                      }}
                    >
                      {seconds}s
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Action area: single full-width button when params are expanded,
              two-button row [Edit params | Action] when collapsed. */}
          {paramsExpanded ? (
            <>
              {canStartAuction && (
                <button onClick={handleStartAuction} disabled={loading} style={styles.startButton}>
                  {loading ? 'Starting...' : 'Start Auction'}
                </button>
              )}
              {canRunAnother && (
                <button onClick={handleRunAnotherAuction} disabled={actionLoading !== null} style={styles.startButton}>
                  {actionLoading === 'runAnother' ? 'Starting...' : runAnotherLabel}
                </button>
              )}
              {canRunAgain && (
                <button onClick={handleRunAuctionAgain} disabled={actionLoading !== null} style={styles.runAgainButton}>
                  {actionLoading === 'runAgain' ? 'Starting...' : 'Re-run Auction'}
                </button>
              )}
            </>
          ) : (
            <div style={styles.actionRow}>
              <button onClick={() => setUserExpanded(true)} style={styles.editParamsButton}>
                Edit params
              </button>
              {canRunAnother && (
                <button
                  onClick={handleRunAnotherAuction}
                  disabled={actionLoading !== null}
                  style={styles.actionPrimaryButton}
                >
                  {actionLoading === 'runAnother' ? 'Starting...' : runAnotherLabel}
                </button>
              )}
              {canRunAgain && (
                <button
                  onClick={handleRunAuctionAgain}
                  disabled={actionLoading !== null}
                  style={{ ...styles.actionPrimaryButton, backgroundColor: '#3B82F6' }}
                >
                  {actionLoading === 'runAgain' ? 'Starting...' : 'Re-run Auction'}
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {showBinPanel && (
        <div style={styles.panel}>
          <div style={styles.paramRow}>
            <div style={styles.paramField}>
              <label style={styles.paramLabel}>Price</label>
              <div style={styles.priceInputWrapper}>
                <span style={styles.pricePrefix}>$</span>
                <input
                  type="number"
                  value={binPrice}
                  onChange={(e) => setBinPrice(Number(e.target.value))}
                  min={1}
                  style={styles.input}
                />
              </div>
            </div>
            <div style={styles.paramField}>
              <label style={styles.paramLabel}>
                Quantity {selectedListing && <span style={styles.stockHint}>({selectedListing.stock} avail)</span>}
              </label>
              <div style={styles.quantityInputWrapper}>
                <input
                  type="number"
                  value={binQuantity}
                  onChange={(e) => {
                    const maxStock = selectedListing?.stock || 1;
                    setBinQuantity(Math.max(1, Math.min(maxStock, Number(e.target.value))));
                  }}
                  min={1}
                  max={selectedListing?.stock || 1}
                  style={styles.input}
                />
                <button
                  onClick={() => setBinQuantity(selectedListing?.stock || 1)}
                  style={styles.allButton}
                  type="button"
                >
                  All
                </button>
              </div>
            </div>
          </div>

          <div style={styles.toggleRow}>
            <div style={styles.toggleInfo}>
              <span style={styles.toggleLabel}>On Sale</span>
              <span style={styles.toggleHint}>Apply a discount</span>
            </div>
            <button
              onClick={() => setBinSaleEnabled(!binSaleEnabled)}
              style={{ ...styles.toggle, backgroundColor: binSaleEnabled ? '#3B82F6' : 'rgba(255,255,255,0.25)' }}
              aria-label="Toggle on-sale"
            >
              <div style={{
                ...styles.toggleKnob,
                transform: binSaleEnabled ? 'translateX(20px)' : 'translateX(0)',
              }} />
            </button>
          </div>

          {binSaleEnabled && (
            <div style={styles.saleOptions}>
              <div style={styles.saleTypeRow}>
                <button
                  onClick={() => setBinSaleType('PERCENT')}
                  style={{ ...styles.saleTypeButton, ...(binSaleType === 'PERCENT' ? styles.saleTypeButtonActive : {}) }}
                >
                  % Off
                </button>
                <button
                  onClick={() => setBinSaleType('AMOUNT')}
                  style={{ ...styles.saleTypeButton, ...(binSaleType === 'AMOUNT' ? styles.saleTypeButtonActive : {}) }}
                >
                  $ Off
                </button>
              </div>
              <div style={styles.saleAmountRow}>
                <label style={styles.paramLabel}>
                  {binSaleType === 'PERCENT' ? 'Percent Off' : 'Amount Off'}
                </label>
                <div style={styles.priceInputWrapper}>
                  <span style={styles.pricePrefix}>{binSaleType === 'PERCENT' ? '%' : '$'}</span>
                  <input
                    type="number"
                    value={binSaleAmount}
                    onChange={(e) => setBinSaleAmount(Number(e.target.value))}
                    min={0}
                    max={binSaleType === 'PERCENT' ? 99 : binPrice - 1}
                    style={styles.input}
                  />
                </div>
              </div>
              <div style={styles.finalPricePreview}>
                Final Price: <strong>${calculateFinalPrice().toFixed(2)}</strong>
              </div>
            </div>
          )}

          <button onClick={handleStartBinSale} disabled={loading} style={styles.startBinButton}>
            {loading ? 'Starting...' : `Start Sale — $${calculateFinalPrice().toFixed(2)}`}
          </button>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    backgroundColor: 'rgba(255,255,255,0.12)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    borderRadius: 12,
    padding: 10,
    border: '1px solid rgba(255,255,255,0.1)',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    color: '#fff',
  },
  modeSelector: {
    display: 'flex',
    gap: 4,
    backgroundColor: 'rgba(255,255,255,0.08)',
    padding: 3,
    borderRadius: 10,
  },
  modeButton: {
    flex: 1,
    padding: '8px 12px',
    fontSize: 12,
    fontWeight: 600,
    backgroundColor: 'transparent',
    color: 'rgba(255,255,255,0.7)',
    border: 'none',
    borderRadius: 8,
    cursor: 'pointer',
    transition: 'all 0.15s',
  },
  modeButtonActive: {
    backgroundColor: 'rgba(255,255,255,0.18)',
    color: '#fff',
    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
  },
  panel: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  paramRow: {
    display: 'flex',
    gap: 8,
  },
  paramField: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  paramLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: 'rgba(255,255,255,0.7)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  priceInputWrapper: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: 'rgba(255,255,255,0.1)',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: 8,
    overflow: 'hidden',
  },
  pricePrefix: {
    padding: '8px 10px',
    fontSize: 13,
    fontWeight: 500,
    color: 'rgba(255,255,255,0.6)',
    backgroundColor: 'rgba(0,0,0,0.2)',
    borderRight: '1px solid rgba(255,255,255,0.15)',
  },
  suffix: {
    padding: '8px 10px',
    fontSize: 13,
    fontWeight: 500,
    color: 'rgba(255,255,255,0.6)',
    backgroundColor: 'rgba(0,0,0,0.2)',
    borderLeft: '1px solid rgba(255,255,255,0.15)',
  },
  input: {
    flex: 1,
    padding: '8px 10px',
    fontSize: 13,
    fontWeight: 500,
    border: 'none',
    outline: 'none',
    backgroundColor: 'transparent',
    color: '#fff',
    width: '100%',
    boxSizing: 'border-box',
  },
  quantityInputWrapper: {
    display: 'flex',
    gap: 6,
    alignItems: 'stretch',
  },
  allButton: {
    padding: '0 12px',
    fontSize: 12,
    fontWeight: 600,
    backgroundColor: 'rgba(255,255,255,0.15)',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  stockHint: {
    fontWeight: 400,
    color: 'rgba(255,255,255,0.5)',
    fontSize: 10,
    textTransform: 'none' as const,
    letterSpacing: 0,
  },
  toggleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 12px',
    backgroundColor: 'rgba(255,255,255,0.08)',
    borderRadius: 10,
  },
  toggleInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
  },
  toggleLabel: {
    fontSize: 13,
    fontWeight: 600,
    color: '#fff',
  },
  toggleHint: {
    fontSize: 11,
    color: 'rgba(255,255,255,0.55)',
  },
  toggle: {
    width: 44,
    height: 24,
    borderRadius: 12,
    border: 'none',
    cursor: 'pointer',
    position: 'relative',
    transition: 'background-color 0.2s',
    padding: 2,
  },
  toggleKnob: {
    width: 20,
    height: 20,
    borderRadius: '50%',
    backgroundColor: '#fff',
    boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
    transition: 'transform 0.2s',
  },
  counterbidSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  counterbidLabelRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  suddenDeathPill: {
    fontSize: 10,
    fontWeight: 600,
    color: '#fff',
    backgroundColor: 'rgba(236,72,153,0.85)',
    padding: '2px 8px',
    borderRadius: 999,
    letterSpacing: '0.3px',
    textTransform: 'uppercase' as const,
  },
  counterbidButtons: {
    display: 'flex',
    gap: 6,
  },
  counterbidButton: {
    flex: 1,
    padding: '8px 12px',
    fontSize: 13,
    fontWeight: 600,
    backgroundColor: 'rgba(255,255,255,0.1)',
    color: 'rgba(255,255,255,0.85)',
    // Longhand so React tracks borderColor independently — see storeItem.
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: 'rgba(255,255,255,0.15)',
    borderRadius: 8,
    cursor: 'pointer',
    transition: 'all 0.15s',
  },
  counterbidButtonActive: {
    backgroundColor: '#3B82F6',
    color: '#fff',
    borderColor: '#3B82F6',
  },
  startButton: {
    width: '100%',
    padding: '12px 20px',
    backgroundColor: '#22C55E',
    color: '#fff',
    border: 'none',
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  },
  runAgainButton: {
    width: '100%',
    padding: '12px 20px',
    backgroundColor: '#3B82F6',
    color: '#fff',
    border: 'none',
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  },
  startBinButton: {
    width: '100%',
    padding: '12px 20px',
    backgroundColor: '#F59E0B',
    color: '#fff',
    border: 'none',
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  },
  saleOptions: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    padding: 10,
    backgroundColor: 'rgba(255,255,255,0.06)',
    borderRadius: 10,
    border: '1px solid rgba(255,255,255,0.1)',
  },
  saleTypeRow: {
    display: 'flex',
    gap: 6,
  },
  saleTypeButton: {
    flex: 1,
    padding: '8px 12px',
    fontSize: 13,
    fontWeight: 600,
    backgroundColor: 'rgba(255,255,255,0.1)',
    color: 'rgba(255,255,255,0.85)',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: 'rgba(255,255,255,0.15)',
    borderRadius: 8,
    cursor: 'pointer',
  },
  saleTypeButtonActive: {
    backgroundColor: '#3B82F6',
    color: '#fff',
    borderColor: '#3B82F6',
  },
  saleAmountRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  finalPricePreview: {
    fontSize: 13,
    color: '#fff',
    textAlign: 'center' as const,
    padding: 6,
    backgroundColor: 'rgba(255,255,255,0.1)',
    borderRadius: 6,
  },
  errorMessage: {
    padding: '8px 12px',
    backgroundColor: 'rgba(220,38,38,0.85)',
    color: '#fff',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 500,
  },
  actionRow: {
    display: 'flex',
    gap: 6,
  },
  editParamsButton: {
    flex: 1,
    padding: '12px',
    backgroundColor: 'rgba(255,255,255,0.15)',
    color: '#fff',
    border: 'none',
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  },
  actionPrimaryButton: {
    flex: 2,
    padding: '12px',
    backgroundColor: '#22C55E',
    color: '#fff',
    border: 'none',
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  },
};

